import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Observable, of } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';

import { HasId } from '../basic-data-management/has-id.interface';
import { TreeNode } from '../basic-data-management/tree-node.entity';

// Representation applatie des objets
export class FlattenedNode {
	constructor(
		public expandable: boolean,
		public entity: HasId,
		public level: number,
		public id: number
	) { }
}

// Evenement de damande deplacement d'un item
export class ItemMoveRequestEvent {
	constructor(
		public id: number,
		public targetParentId: number,
		public targetIndex: number
	) { }
}

// Evenement de demande de modification d'un item
export class ItemModificationRequestEvent {
	constructor(
		public id: number
	) { }
}

// Evenement de demande d'archivage d'un item
export class ItemArchivingRequestEvent {
	constructor(
		public id: number,
		public isNode: boolean
	) { }
}

// Evenement de demande d'archivage d'un item
export class ItemUnarchivingRequestEvent {
	constructor(
		public id: number,
		public isNode: boolean
	) { }
}

// Evenement de demande de suppression d'un item
export class ItemDeletionRequestEvent {
	constructor(
		public id: number,
		public isNode: boolean
	) { }
}

// Localisation d'un node dans l'arbre
class Cursor {
	constructor(
		public parentId: number,
		public siblings: TreeNode<HasId>[],
		public index: number
	) { }
	
	get(): TreeNode<HasId> {
		return this.siblings[this.index];
	}

	extract(): TreeNode<HasId> {
		return this.siblings.splice(this.index, 1)[0];
	}

	insert(node: TreeNode<HasId>, after: boolean): number {
		const insertIndex = after ? this.index + 1 : this.index;
		this.siblings.splice(insertIndex, 0, node);
		return insertIndex;
	}
}

@Component({
	selector: 'drag-drop-tree',
	templateUrl: 'drag-drop-tree.html',
	// Voir la feuille de style globale pour les styles
	styleUrls: ['drag-drop-tree.css']
})
export class DragDropTreeComponent {
	//////////////////////////////////////////////
	// Configuration du composant par le parent //
	//////////////////////////////////////////////
	
	// La liste des noeuds racine
	private _rootNodes: TreeNode<HasId>[] = [];
	@Input() set rootNodes(values: TreeNode<HasId>[]) {
		this._rootNodes = values ? values : [];
		this.onDataChange();
	}
	get rootNodes(): TreeNode<HasId>[] {
		return this._rootNodes;
	}
	
	// La fonction a utiliser pour obtenir le libelle d'une entite
	@Input() labelGetter!: (entity: any) => string;

	// La fonction a utiliser pour obtenir le statut d'archivage d'une entite
	@Input() obsoleteGetter!: (entity: any) => boolean;
	
	// La fonction a utiliser pour obtenir le libelle d'une entite (optionel)
	@Input() contextDescGetter!: (entity: any) => string;
	
	// Emetteur d'evenement pour le deplacement d'un item
	@Output() itemMoveRequested = new EventEmitter<ItemMoveRequestEvent>();
	
	// Emetteur d'evenement pour la demande de modification d'un item
	@Output() itemModificationRequested = new EventEmitter<ItemModificationRequestEvent>();

	// Emetteur d'evenement pour la demande d'archivage d'un item
	@Output() itemArchivingRequested = new EventEmitter<ItemArchivingRequestEvent>();

	// Emetteur d'evenement pour la demande de suppression d'un item
	@Output() itemDeletionRequested = new EventEmitter<ItemDeletionRequestEvent>();

	// Emetteur d'evenement pour la demande d'archivage d'un item
	@Output() itemUnarchivingRequested = new EventEmitter<ItemUnarchivingRequestEvent>();
	
	////////////////////////////////////////////
	// Points d'acces aux donnees pour la vue //
	////////////////////////////////////////////

	// Fonctions simplifiant l'utilisation du labelGetter et du contextDescGetter dans l'interface
	getLabel: (entity: any) => string = (entity) => entity ? this.labelGetter(entity) : '';
	getObsolete: (entity: any) => boolean = (entity) => entity ? this.obsoleteGetter(entity) : false;
	getContextDesc: (entity: any) => string = (entity) => entity ? this.contextDescGetter(entity) : '';
	
	// Elements structurels utilises par l'arbre pour manipuler les donnees 
	treeControl: FlatTreeControl<FlattenedNode>;
	dataSource: MatTreeFlatDataSource<TreeNode<HasId>, FlattenedNode>;
	
	////////////////////////////////
	// Gestion du menu contextuel //
	////////////////////////////////
	
	// Emplacement d'affichage du menu contextuel
	contextMenuPosition =  {x: '0', y: '0'};
	
	// Item auquel le menu contextuel fait reference
	contextMenuItem!: FlattenedNode;

	// Reference vers le MatMenuTrigger pour permettre l'ouverture programatique du menu contextuel
	@ViewChild(MatMenuTrigger, {static: true}) matMenuTrigger!: MatMenuTrigger;

	////////////
	// Divers //
	////////////

	// Lambdas utilisees dans diverses fonctions
	private flattenNode = (node: TreeNode<HasId>, level: number) => new FlattenedNode(!!node.children && node.children.length > 0, node.entity, level, node.entity.id);
	private getFlattenedNodeLevel = (node: FlattenedNode) => node.level;
	private isFlattenedNodeExpandable = (node: FlattenedNode) => node.expandable;
	private getNodeChildren = (node: TreeNode<HasId>): Observable<TreeNode<HasId>[]> => of(node.children);

	constructor() {
		const treeFlattener = new MatTreeFlattener(this.flattenNode, this.getFlattenedNodeLevel, this.isFlattenedNodeExpandable, this.getNodeChildren);
		this.treeControl = new FlatTreeControl<FlattenedNode>(this.getFlattenedNodeLevel, this.isFlattenedNodeExpandable);
		this.dataSource = new MatTreeFlatDataSource(this.treeControl, treeFlattener);
		
		this.onDataChange();
	}
	
	////////////////////////////
	// Gestion des evenements //
	////////////////////////////
	
	// Gere les evenements de changement de donnnees sources (que ce soit par le parent ou par une operation drag-drop de l'utilisateur)
	onDataChange() {
		let data = this._rootNodes;
		
		// Memoriser les noeuds deployes
		let expanded: number[] = [];
		this.treeControl.expansionModel.selected.forEach(flattenedNode => expanded.push(flattenedNode.id));

		// Fixer les nouvelles donnees dans le dataSource de l'arbre
		this.dataSource.data = data;

		// Consulter la liste des noeuds deployees precedemment
		expanded.forEach((id) => {
			// Si ce noeud existe dans les nouvelles donnees...
			const node = this.treeControl.dataNodes.find((node) => node.id === id);
			if (node !== undefined) {
				// Le deployer de meme
				this.treeControl.expand(node);
			}
		});
	}

	// Gere l'evenement de drag-drop par un utilisateur
	onItemDrop(event: CdkDragDrop<string[]>) {
		// Si l'objet a ete drop en dehors du conteneur, ignore l'evenement
		if (!event.isPointerOverContainer)
			return;

		// Si la position de l'element n'a pas change dans le drag and drop, annuler
		if (event.previousIndex === event.currentIndex)
			return;

		// Determiner si on drop plus bas que la position d'origine
		let dropLower = event.previousIndex < event.currentIndex;

		// Recuperer le noeud en cours de deplacement
		const movedFlattenedNode: FlattenedNode = event.item.data;
		const movedNodeId = movedFlattenedNode.id;

		// Localiser ce meme noeud
		const tree = this.dataSource.data;
		const movedNodeCursor = this.locateNode(tree, movedNodeId);

		// Recuperer la liste des noeuds/feuilles visibles
		const visibleNodes = this.visibleNodes();

		// Determiner l'emplacement ou deposer le noeud
		let newIndex:number;
		let newParentId:number;
		if (event.currentIndex >= visibleNodes.length) {
			// Cas special: on a place le noeud droppe tout a la fin de l'affichage

			// Retirer le noeud de son parent
			const movedNode = movedNodeCursor.extract();

			// Recuperer le dernier element affiche 
			const targetNode = visibleNodes[visibleNodes.length - 1];
			const targetNodeId = targetNode.entity.id;

			// Recuperer la collection de fils auquel cet element appartient
			const targetNodeCursor = this.locateNode(tree, targetNodeId);
		
			// Y inserer le noeud deplace
			newIndex = targetNodeCursor.insert(movedNode, dropLower);
			newParentId = targetNodeCursor.parentId;
		} else {
			// Cas ordinaire: le noeud a ete deplace sur la position d'un autre noeud

			// Determiner quel est le noeud sur lequel l'utilisateur tente de faire le drop
			const targetNode = visibleNodes[event.currentIndex];
			const targetNodeId = targetNode.entity.id;
			
			// Si le noeud deplace a des fils
			const movedNodeChildren = movedNodeCursor.get().children;
			if (!!movedNodeChildren) {
				// Il faut alors s'assurer que le noeud cible ne fait par partie des descendants
				if (this._locateNode(-1, movedNodeChildren, targetNodeId)) {
					// Si c'est malheureusement le cas, il faut interdire le deplacement
					return ;
				}
			}

			// Retirer le noeud de son parent
			const movedNode = movedNodeCursor.extract();

			// Recuperer un curseur sur cet element
			let targetNodeCursor = this.locateNode(tree, targetNodeId);
			
			// Obtenir le curseur de la position cible
			if (dropLower && this.isExpanded(targetNode)) {
				// Cas special ou on depose sous un noeud ouvert
					
				// Au lieu de deposer le noeud comme frere suivant du noeud-cible, il faut alors le placer comme premier de ses fils
				targetNodeCursor = new Cursor(targetNode.entity.id, targetNode.children, 0);
				dropLower = false;
			} else {
				// Sinon, cas nominal, on prend comme curseur tout simplement le noeud cible par le drop
				targetNodeCursor = this.locateNode(tree, targetNodeId);
			}
		
			// Y inserer le noeud deplace
			newIndex = targetNodeCursor.insert(movedNode, dropLower);
			newParentId = targetNodeCursor.parentId;
		}
		
		// Lancer l'evenement de deplacement
		this.itemMoveRequested.emit(new ItemMoveRequestEvent(movedNodeId, newParentId, newIndex));

		// Reconstruire l'affichage
		this._rootNodes = tree;
		this.onDataChange();
	}
	
	// Gere l'evenement de clic-droit sur un node
	onRightClick(event: MouseEvent, node: FlattenedNode) {
		// Annule l'affichage normal d'un clic-droit 
		event.preventDefault();
		// Fixer l'emplacement du menu contextuel
		this.contextMenuPosition.x = event.clientX + 'px';
		this.contextMenuPosition.y = event.clientY + 'px';

		// Transmettre le noeud comme donnee de base du menu
		this.contextMenuItem = node;

		// Ouvrir le menu
		this.matMenuTrigger.openMenu();
	}
	
	// Gere l'evenement de clic sur un bouton contextuel "Modifier"
	onModifyClick(_event: MouseEvent, node: FlattenedNode) {
		this.itemModificationRequested.emit(new ItemModificationRequestEvent(node.entity.id));
	}

	// Gere l'evenement de clic sur un bouton contextuel "Archiver"
	onArchiveClick(_event: MouseEvent, node: FlattenedNode) {
		this.itemArchivingRequested.emit(new ItemArchivingRequestEvent(node.entity.id, node.expandable));
	}

	// Gere l'evenement de clic sur un bouton contextuel "Desarchiver"
	onUnarchiveClick(_event: MouseEvent, node: FlattenedNode) {
		this.itemUnarchivingRequested.emit(new ItemUnarchivingRequestEvent(node.entity.id, node.expandable));
	}

	// Gere l'evenement de clic sur un bouton contextuel "Supprimer"
	onDeleteClick(_event: MouseEvent, node: FlattenedNode) {
		this.itemDeletionRequested.emit(new ItemDeletionRequestEvent(node.entity.id, node.expandable));
	}
	
	///////////////////////////////////////////////////
	// Fonctions d'aide a la manipulation de l'arbre //
	///////////////////////////////////////////////////

	// Genere un tableau correspondant a la liste des noeuds/feuilles actuellement visibles de l'arbre
	private visibleNodes(): TreeNode<HasId>[] {
		const result: TreeNode<HasId>[] = [];

		function addExpandedChildren(node: TreeNode<HasId>, expanded: number[]) {
			result.push(node);
			if (expanded.includes(node.entity.id)) {
				node.children.map((child) => addExpandedChildren(child, expanded));
			}
		}

		let expanded: number[] = [];
		this.treeControl.expansionModel.selected.forEach(node => expanded.push(node.id));
		this.dataSource.data.forEach((node) => {
			addExpandedChildren(node, expanded);
		});

		return result;
	}

	// Localise un noeud dans l'arbre et renvoie le resultat sous forme d'un curseur pour simplifier les manipulations
	private locateNode(tree: TreeNode<HasId>[], id: number): Cursor {
		const result = this._locateNode(-1, tree, id);
		if (result)
			return result;
		throw new Error("Element " + id + " introuvable dans l'arbre");
	}
	private _locateNode(rootId: number, tree: TreeNode<HasId>[], id: number): Cursor | undefined {
		// Est-ce que le node fait partie du niveau racine de l'arbre?
		for (let i = 0; i < tree.length; i++) {
			let entity = tree[i];
			if (entity.entity.id === id) {
				// Si on le trouve, alors le resultat est ce niveau racine
				return new Cursor(rootId, tree, i);
			}
		}

		// Sinon on va rechercher les sous-niveaux de chaque element racine
		for (let i = 0; i < tree.length; i++) {
			let node = tree[i];
			if (node.children) {
				let subResult = this._locateNode(node.entity.id, node.children, id);
				if (subResult) {
					return subResult;
				}
			}
		}

		return undefined;
	}
	
	// Determiner si un noeud est ouvert ou non
	private isExpanded(node: TreeNode<HasId>): boolean {
		// Un noeud qui n'a pas de fils ne peut pas etre ouvert
		if (!node.children || node.children.length === 0) {
			return false;
		}
		
		const expandedFlatNodes = this.treeControl.expansionModel.selected;
		const matchingExpandedFlatNodes = expandedFlatNodes.filter((flattenedNode) => flattenedNode.id === node.entity.id);
		
		return matchingExpandedFlatNodes.length > 0;
	}
	
	isObsolete(node: FlattenedNode) {
		if (node) {
			return this.getObsolete(node.entity);
		}
		return false;
	}
}